import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Flex, Image, Text, useToast } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { AppBar } from "../../components/mobileUi";
import { DetailTabBar } from "../../components/financeDetail";
import { CenterSpinner, M, Png, PropCell } from "../../components/managementUi";
import {
  DesktopBack,
  DesktopButton,
  DesktopPage,
  DesktopPanel,
  DetailItem,
  DetailSection,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import { resolveImageUrl } from "../../utils/format";
import { getOutsideDetailApi, approveOutsideApi } from "../../api/registerOutsideManager.api";
import { EMP_API_BASE_URL } from "../../config";
import { outsideStatusUi } from "./OutsideApprovalList";

// Header khớp RegisterOutsideManagerInfoHeader của app.
const ManagerInfoHeader = ({ detail }) => {
  const ui = outsideStatusUi(detail?.status);
  const icon =
    detail?.status === "APPROVED"
      ? appImages.icon_check_circle
      : detail?.status === "REJECTED"
      ? appImages.icon_reject_circle
      : null;
  return (
    <Box pt="16px">
      <Flex align="center" flexWrap="wrap" gap="4px">
        <Text fontSize="16px" fontWeight={600} color={M.baseContent}>
          {detail?.employeeObject?.fullName || "-"}
        </Text>
        <Text fontSize="16px" fontWeight={600} color={M.baseContent}>
          ({detail?.employeeObject?.code || "-"})
        </Text>
      </Flex>
      <Flex align="center" flexWrap="wrap" gap="8px" mt="4px">
        <Text fontSize="14px" fontWeight={600} color={M.tertiary}>
          {detail?.createdAt ? moment(detail.createdAt).format("DD/MM/YYYY") : "--/--/----"}
        </Text>
        <Text fontSize="14px" fontWeight={600} color={M.baseContent}>
          -
        </Text>
        <Flex align="center" bg={ui.bg} borderRadius="6px" px="8px" py="3px" gap="8px">
          {icon ? <Png src={icon} size={18} /> : null}
          <Text fontSize="14px" fontWeight={600} color={ui.color}>
            {ui.label}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
};

const OutsideApprovalDetail = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { id } = useParams();
  const toast = useToast();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getOutsideDetailApi(id)
      .then((d) => setDetail(d || null))
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [id]);

  const doApprove = useCallback(
    async (status) => {
      setSubmitting(true);
      try {
        await approveOutsideApi(id, status);
        toast({ title: "Duyệt Thành công", status: "success", duration: 2500, position: "top" });
        navigate(-1);
      } catch (e) {
        toast({ title: e?.message || "Duyệt thất bại", status: "error", duration: 3000, position: "top" });
      } finally {
        setSubmitting(false);
      }
    },
    [id, navigate, toast]
  );

  const confirmImages = useMemo(
    () => (Array.isArray(detail?.confirmImage) ? detail.confirmImage : []),
    [detail?.confirmImage]
  );

  // Tab "Ảnh xác nhận" chỉ hiện khi có ảnh - khớp app.
  const tabs = useMemo(() => {
    const list = [{ key: "overview", title: "Tổng quan" }];
    if (confirmImages.length) list.push({ key: "images", title: "Ảnh xác nhận" });
    return list;
  }, [confirmImages.length]);

  useEffect(() => {
    if (tab >= tabs.length) setTab(0);
  }, [tabs.length, tab]);

  const emp = detail?.employee || {};

  // ===================== Bản WEB (desktop) =====================
  // Desktop rộng nên hiển thị cả tổng quan và ảnh xác nhận, bỏ tab.
  if (isDesktop) {
    const ui = outsideStatusUi(detail?.status);
    return (
      <DesktopPage
        title="Chi tiết đơn đăng ký chấm công ngoài"
        right={<DesktopBack onClick={() => navigate(-1)} />}
      >
        {loading ? (
          <CenterSpinner />
        ) : !detail ? (
          <Text fontSize="14px" color={M.subTitle} textAlign="center" pt="40px">
            Không tìm thấy thông tin đơn chấm công ngoài
          </Text>
        ) : (
          <>
            <DetailSection
              title={`${detail?.employeeObject?.fullName || emp?.fullName || "-"} (${
                detail?.employeeObject?.code || emp?.code || "-"
              })`}
              right={
                <Flex align="center" gap="12px">
                  <Flex align="center" bg={ui.bg} borderRadius="6px" px="10px" py="4px">
                    <Text fontSize="14px" fontWeight={600} color={ui.color}>
                      {ui.label}
                    </Text>
                  </Flex>
                  {detail?.status === "PENDING" && (
                    <>
                      <DesktopButton
                        isDisabled={submitting}
                        onClick={() => !submitting && doApprove("APPROVED")}
                      >
                        Phê duyệt
                      </DesktopButton>
                      <DesktopButton
                        variant="danger"
                        isDisabled={submitting}
                        onClick={() => !submitting && doApprove("REJECTED")}
                      >
                        Từ chối
                      </DesktopButton>
                    </>
                  )}
                </Flex>
              }
            >
              <DetailItem
                label="Ngày tạo"
                value={detail?.createdAt ? moment(detail.createdAt).format("DD/MM/YYYY") : "-"}
              />
              <DetailItem label="Khối" value={emp?.saleBlock?.name} />
              <DetailItem label="Vùng" value={emp?.saleArea?.name} />
              <DetailItem label="Địa điểm" value={detail?.checkinPlace} />
              <DetailItem label="Số công" value={detail?.workingDayRate ?? 0} />
              <DetailItem label="Lý do" value={detail?.comment} />
            </DetailSection>

            {confirmImages.length > 0 && (
              <DesktopPanel>
                <Text fontSize="16px" fontWeight={600} color={M.baseContent} mb="16px">
                  Ảnh xác nhận
                </Text>
                <Flex gap="16px" flexWrap="wrap">
                  {confirmImages.map((p, i) => (
                    <Box
                      key={`${p}-${i}`}
                      border="1px solid"
                      borderColor={M.border}
                      borderRadius="12px"
                      overflow="hidden"
                    >
                      <Image
                        src={resolveImageUrl(p, EMP_API_BASE_URL)}
                        alt=""
                        w="280px"
                        h="200px"
                        objectFit="cover"
                        bg="#F8F8F8"
                      />
                    </Box>
                  ))}
                </Flex>
              </DesktopPanel>
            )}
          </>
        )}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb={detail?.status === "PENDING" ? "100px" : "40px"}>
      <AppBar title="Chi tiết đơn đăng ký chấm công ngoài" />

      {loading ? (
        <CenterSpinner />
      ) : !detail ? (
        <Text fontSize="14px" color={M.subTitle} textAlign="center" pt="40px">
          Không tìm thấy thông tin đơn chấm công ngoài
        </Text>
      ) : (
        <>
          <DetailTabBar tabs={tabs} index={tab} onChange={setTab} />

          {tabs[tab]?.key === "overview" ? (
            <Box px="16px">
              <ManagerInfoHeader detail={detail} />
              <PropCell label="Khối" value={emp?.saleBlock?.name} />
              <PropCell label="Vùng" value={emp?.saleArea?.name} />
              <PropCell label="Địa điểm" value={detail?.checkinPlace} />
              <PropCell label="Số công" value={detail?.workingDayRate ?? 0} />
              <PropCell label="Lý do" value={detail?.comment} borderBottom={false} />
            </Box>
          ) : (
            <Box px="16px" pt="16px">
              <Text fontSize="14px" fontWeight={600} color={M.baseContent}>
                Ảnh xác nhận
              </Text>
              <Box h="16px" />
              <Flex direction="column" gap="16px">
                {confirmImages.map((p, i) => (
                  <Box key={`${p}-${i}`} border="1px solid" borderColor={M.border} borderRadius="12px" overflow="hidden">
                    <Image
                      src={resolveImageUrl(p, EMP_API_BASE_URL)}
                      alt=""
                      w="100%"
                      h="240px"
                      objectFit="cover"
                      bg="#F8F8F8"
                    />
                  </Box>
                ))}
              </Flex>
            </Box>
          )}

          {/* Thanh hành động cố định đáy màn - khớp app */}
          {detail?.status === "PENDING" && (
            <Flex
              position="fixed"
              bottom="0"
              left="50%"
              transform="translateX(-50%)"
              w="100%"
              maxW="480px"
              bg="white"
              zIndex={20}
              justify="space-evenly"
              align="center"
              p="16px"
              gap="12px"
            >
              <Flex
                as="button"
                type="button"
                flex="1"
                maxW="170px"
                h="48px"
                align="center"
                justify="center"
                gap="8px"
                borderRadius="8px"
                border="1px solid"
                borderColor={M.border}
                opacity={submitting ? 0.6 : 1}
                onClick={() => !submitting && doApprove("APPROVED")}
              >
                <Png src={appImages.icon_check_circle} size={24} />
                <Text fontSize="16px" fontWeight={600} color={M.baseContent}>
                  Phê duyệt
                </Text>
              </Flex>
              <Flex
                as="button"
                type="button"
                flex="1"
                maxW="170px"
                h="48px"
                align="center"
                justify="center"
                gap="8px"
                borderRadius="8px"
                border="1px solid"
                borderColor={M.border}
                opacity={submitting ? 0.6 : 1}
                onClick={() => !submitting && doApprove("REJECTED")}
              >
                <Png src={appImages.icon_reject_circle} size={24} />
                <Text fontSize="16px" fontWeight={600} color={M.baseContent}>
                  Từ chối
                </Text>
              </Flex>
            </Flex>
          )}
        </>
      )}
    </Box>
  );
};

export default OutsideApprovalDetail;
